import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import logo from '@/assets/logo.png';

const Header: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Don't show header on login/register pages
  if (location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src={logo} alt="Logo" className="h-8 w-8 rounded-[16px]" />
            <span className="text-xl font-bold text-gray-900">
              Voglio la <span className="text-blue-600">Fibra</span>
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link
              to="/map"
              className="text-gray-600 hover:text-blue-600 transition-colors"
            >
              Mappa
            </Link>
            <Link
              to="/privacy"
              className="text-gray-600 hover:text-blue-600 transition-colors"
            >
              Privacy Policy
            </Link>

            {isAuthenticated ? (
              <>
                {user?.role === 'admin' ? (
                  <Link
                    to="/admin/dashboard"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Dashboard Admin
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/my-reports"
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      Le Mie Segnalazioni
                    </Link>
                    <Link
                      to="/report"
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      Nuova Segnalazione
                    </Link>
                  </>
                )}

                {/* User info and logout */}
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-700">
                    Ciao, {user?.name || user?.email || user?.username}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-sm transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  Accedi
                </Link>
                <Link
                  to="/register"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm transition-colors"
                >
                  Registrati
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100 transition-colors"
              aria-label="Menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-3">
              <Link
                to="/map"
                className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                onClick={closeMobileMenu}
              >
                Mappa
              </Link>
              <Link
                to="/privacy"
                className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                onClick={closeMobileMenu}
              >
                Privacy Policy
              </Link>

              {isAuthenticated ? (
                <>
                  {user?.role === 'admin' ? (
                    <Link
                      to="/admin/dashboard"
                      className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                      onClick={closeMobileMenu}
                    >
                      Dashboard Admin
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/my-reports"
                        className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                        onClick={closeMobileMenu}
                      >
                        Le Mie Segnalazioni
                      </Link>
                      <Link
                        to="/report"
                        className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                        onClick={closeMobileMenu}
                      >
                        Nuova Segnalazione
                      </Link>
                    </>
                  )}

                  {/* User info and logout for mobile */}
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="text-sm text-gray-700 mb-2">
                      Ciao, {user?.name || user?.email || user?.username}
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        closeMobileMenu();
                      }}
                      className="w-full text-left bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                  <Link
                    to="/login"
                    className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                    onClick={closeMobileMenu}
                  >
                    Accedi
                  </Link>
                  <Link
                    to="/register"
                    className="block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm transition-colors text-center"
                    onClick={closeMobileMenu}
                  >
                    Registrati
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;